import { useEffect } from 'react';
import { SITE_KEYWORDS } from '../config/seoKeywords';

export const SITE_URL = 'https://swarvinnfoods.co.uk';
export const SITE_NAME = 'Swarvinn Impex';
export const SITE_BRAND = 'Swarvinn Foods';
export const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/images/logo-header.png`;

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

function absoluteUrl(path) {
  if (!path || path === '/') return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function cleanDescription(value) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 300);
}

function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_SOCIAL_IMAGE,
  keywords = SITE_KEYWORDS,
  type = 'website',
  noindex = false,
  schema,
}) {
  useEffect(() => {
    const canonical = absoluteUrl(path);
    const socialImage = absoluteUrl(image);
    const pageTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const pageDescription = cleanDescription(description);

    document.title = pageTitle;
    document.documentElement.lang = 'en-GB';

    upsertMeta('meta[name="description"]', { name: 'description', content: pageDescription });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_GB' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: pageTitle });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: pageDescription,
    });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: socialImage });
    upsertMeta('meta[property="og:image:alt"]', {
      property: 'og:image:alt',
      content: `${SITE_NAME} — Indian grocery export to the United Kingdom`,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: pageTitle });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: pageDescription,
    });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: socialImage });
    setCanonical(canonical);

    document.head.querySelectorAll('script[data-page-schema]').forEach((element) => element.remove());

    const schemas = (Array.isArray(schema) ? schema : [schema]).filter(Boolean);
    schemas.forEach((value) => {
      const element = document.createElement('script');
      element.type = 'application/ld+json';
      element.dataset.pageSchema = 'true';
      element.textContent = JSON.stringify(value).replace(/</g, '\\u003c');
      document.head.appendChild(element);
    });

    return () => {
      document.head.querySelectorAll('script[data-page-schema]').forEach((element) => element.remove());
    };
  }, [description, image, keywords, noindex, path, schema, title, type]);

  return null;
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export default Seo;
